import { TextField } from "@mui/material";
import { useEffect, useState, useRef } from "react";

interface WeightInputProps {
  value: number;
  onChange: (weight: number) => void;
  disabled: boolean;
  bgColor: string;
  borderColor: string;
  onBlur?: () => void;
  onFocus?: () => void;
}

export const WeightInput = ({
  value,
  onChange,
  disabled,
  bgColor,
  borderColor,
  onBlur = () => {},
  onFocus = () => {},
}: WeightInputProps) => {
  const [localValue, setLocalValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isFocused) {
      const numValue = Number(value);
      setLocalValue(isNaN(numValue) ? "0" : numValue.toString());
    }
  }, [value, isFocused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue === "") {
      setLocalValue("");
      onChange(0);
      return;
    }

    if (inputValue === "0") {
      setLocalValue("0");
      onChange(0);
      return;
    }

    const numericValue = parseFloat(inputValue);
    if (!isNaN(numericValue) && numericValue >= 0) {
      setLocalValue(inputValue);
      onChange(numericValue);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus();
    if (localValue === "0") {
      setLocalValue("");
    }
    e.target.select();
  };

  const handleBlur = () => {
    setIsFocused(false);

    const numValue = Number(localValue);
    const validValue = isNaN(numValue) || numValue < 0 ? 0 : numValue;
    setLocalValue(validValue.toString());

    if (onChange && validValue !== value) {
      onChange(validValue);
    }
    onBlur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      inputRef.current?.blur();
    }
  };

  return (
    <TextField
      inputRef={inputRef}
      variant="outlined"
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      size="small"
      fullWidth
      disabled={disabled}
      type="text"
      slotProps={{
        input: {
          style: {
            backgroundColor: disabled ? bgColor : "white",
            borderColor: borderColor,
            height: "40px",
          },
        },
      }}
      inputProps={{
        min: 0,
        style: {
          textAlign: "center",
          padding: "10px",
        },
      }}
    />
  );
};
